import { useEffect, useRef, useState } from 'react'
import type { DragEvent, JSX } from 'react'
import { LOGO_ACCEPT, LOGO_RULE, deleteLogo, logoProblem, putLogo } from '@/api/logo'
import { useMutation } from '@/api/query'
import { useBrandRefresh } from '@/brand/context'
import { may, useActiveTenant, useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { CopyButton } from '@/ui/CopyButton'
import { Field, TextField } from '@/ui/Field'
import { Modal } from '@/ui/Modal'
import { InlineError } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Settings → Branding: the organisation's logo, and where its workspace lives.
 *
 * The logo is what the organisation's own host wears (spec: *Branding*) — the
 * lockup, the login panel, the tab icon — and this is the one place to change
 * it after signup. `PUT`/`DELETE /tenants/{id}/logo` behind `tenant:update`,
 * the same gate the *Organisation* panel uses, and read-only for everybody
 * else: they see the mark and the address, and no control that would 403.
 *
 * **Picked, previewed, then sent** — not sent on pick. A logo replaces what
 * every member sees on every page, so the person choosing it gets to look at
 * it beside the current one before it goes; the wizard's article picture
 * (`ApprovalQueue`) uploads on pick because a picture on a draft is a draft's
 * business. The size and type rule is `api/logo.ts`, shared with the signup
 * form, and refused here before a request is spent.
 *
 * After a write the tenant list is re-read (`workspace_url` and `logo_url`
 * live on `TenantOut`) and `/branding` is refreshed, so the lockup in the rail
 * and the favicon follow without a reload — that is what `useBrandRefresh`
 * exists for.
 *
 * The workspace address is read-only with a copy button: the slug is fixed
 * at signup (`OrganisationPanel` says the same of *Short name*), and the URL
 * is what an owner pastes into an invitation or a bookmark. It is the custom
 * domain once one is verified — Settings → Domain is where that happens.
 */
export function BrandingPanel(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const { reload } = useTenants()
  const refreshBrand = useBrandRefresh()
  const toast = useToast()
  const editable = may(membership, 'tenant:update')

  const [picked, setPicked] = useState<{ file: File; preview: string } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [removing, setRemoving] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  // The pick is dropped when the organisation changes under the panel — a
  // switch from the sidebar keeps this mounted, and a preview chosen for one
  // organisation must not be sent to the next.
  useEffect(() => {
    setPicked(null)
    setProblem(null)
  }, [tenant.id])

  const upload = useMutation((file: File) => putLogo(tenant.id, file))
  const remove = useMutation(() => deleteLogo(tenant.id))

  function chosen(file: File): void {
    upload.reset()
    const refused = logoProblem(file)
    if (refused !== null) {
      setPicked(null)
      setProblem(refused)
      return
    }
    setProblem(null)
    const reader = new FileReader()
    reader.onerror = () => {
      setProblem('That file could not be read from your computer.')
    }
    reader.onload = () => {
      setPicked({ file, preview: typeof reader.result === 'string' ? reader.result : '' })
    }
    reader.readAsDataURL(file)
  }

  function onDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    setDragging(false)
    if (!editable) return
    const file = event.dataTransfer.files[0]
    if (file !== undefined) chosen(file)
  }

  async function send(): Promise<void> {
    if (picked === null) return
    const result = await upload.run(picked.file)
    if (!result.ok) return
    setPicked(null)
    reload()
    await refreshBrand()
    toast.show({
      kind: 'success',
      title: 'Logo saved',
      body: 'It is on your sign-in page and in the tab now.',
    })
  }

  async function clear(): Promise<void> {
    const result = await remove.run(undefined)
    setRemoving(false)
    if (!result.ok) return
    reload()
    await refreshBrand()
    toast.show({
      kind: 'success',
      title: 'Logo removed',
      body: 'The first letter of your name stands in until another is uploaded.',
    })
  }

  return (
    <div className="ar-stack">
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>Branding</h2>
          {editable ? null : <span className="ar-badge ar-badge--neutral">Read only</span>}
        </div>

        <div className="ar-card__body ar-stack">
          <InlineError failure={upload.state.failure ?? remove.state.failure} />

          <Field
            label="Logo"
            help={`${LOGO_RULE} Shown on your sign-in page, in the rail and as the tab icon.`}
            error={problem}
          >
            {(aria) => (
              <div className="ar-stack ar-stack--tight">
                {/* What every member sees today — or the empty state, which
                    is also a state and says what stands in. */}
                <div className="ar-row">
                  {tenant.logo_url ? (
                    <img
                      className="app-logo-preview"
                      src={tenant.logo_url}
                      alt={`${tenant.name} logo`}
                    />
                  ) : (
                    <span className="ar-muted ar-text-body-sm">
                      No logo yet. The first letter of <strong>{tenant.name}</strong> stands in on
                      the sign-in page and in the tab.
                    </span>
                  )}
                  {editable && tenant.logo_url && picked === null ? (
                    <Button
                      tone="quiet"
                      size="sm"
                      onClick={() => {
                        setRemoving(true)
                      }}
                    >
                      Remove logo
                    </Button>
                  ) : null}
                </div>

                {editable ? (
                  picked === null ? (
                    /* The dropzone is also the button: a click opens the picker,
                       a drop hands the file straight over, and the keyboard reaches
                       the same picker through the input itself. */
                    <div
                      className="app-dropzone"
                      data-active={dragging ? 'true' : undefined}
                      onDragOver={(event) => {
                        event.preventDefault()
                        setDragging(true)
                      }}
                      onDragLeave={() => {
                        setDragging(false)
                      }}
                      onDrop={onDrop}
                      onClick={() => picker.current?.click()}
                    >
                      <p className="app-dropzone__title">
                        {tenant.logo_url ? 'Drop a new logo here' : 'Drop your logo here'}
                      </p>
                      <p className="ar-muted ar-text-body-sm">
                        or choose a file from your computer
                      </p>
                      <input
                        {...aria}
                        ref={picker}
                        className="ar-visually-hidden"
                        type="file"
                        accept={LOGO_ACCEPT}
                        onClick={(event) => {
                          // The dropzone's own click would open the picker a
                          // second time.
                          event.stopPropagation()
                        }}
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0]
                          // Cleared so the same file chosen twice fires again.
                          event.currentTarget.value = ''
                          if (file !== undefined) chosen(file)
                        }}
                      />
                    </div>
                  ) : (
                    <div className="ar-row">
                      <img
                        className="app-logo-preview"
                        src={picked.preview}
                        alt={`Preview of ${picked.file.name}`}
                      />
                      <span className="ar-text-body-sm">{picked.file.name}</span>
                      <Button
                        tone="primary"
                        size="sm"
                        writes
                        busy={upload.pending}
                        busyLabel="Uploading"
                        onClick={() => {
                          void send()
                        }}
                      >
                        {tenant.logo_url ? 'Replace logo' : 'Upload logo'}
                      </Button>
                      <Button
                        tone="quiet"
                        size="sm"
                        disabled={upload.pending}
                        onClick={() => {
                          setPicked(null)
                          upload.reset()
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )
                ) : null}
              </div>
            )}
          </Field>

          <Field
            label="Workspace address"
            help="Where your team signs in. Fixed at signup; a custom domain of your own is set up under Domain."
          >
            {(aria) => (
              <div className="ar-row">
                <input
                  {...aria}
                  className="ar-input ar-mono"
                  readOnly
                  value={tenant.workspace_url}
                  style={{ flex: '1 1 16rem' }}
                />
                <CopyButton value={tenant.workspace_url} label="workspace address" />
              </div>
            )}
          </Field>

          <div className="ar-grid ar-grid--2">
            <TextField label="Short name" readOnly value={tenant.slug} />
          </div>

          {editable ? null : (
            <p className="ar-field__help" style={{ margin: 0 }}>
              Your role can view the branding. A role granted the organisation settings page can
              change the logo.
            </p>
          )}
        </div>
      </section>

      {removing ? (
        <Modal
          title="Remove the logo?"
          variant="prompt"
          onClose={() => {
            setRemoving(false)
          }}
          footer={
            <>
              <Button
                tone="secondary"
                onClick={() => {
                  setRemoving(false)
                }}
              >
                Keep it
              </Button>
              <Button
                tone="danger"
                writes
                busy={remove.pending}
                busyLabel="Removing"
                onClick={() => {
                  void clear()
                }}
              >
                Remove logo
              </Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            Your sign-in page, the rail and the tab icon go back to the first letter of{' '}
            <strong>{tenant.name}</strong>. Upload another at any time.
          </p>
        </Modal>
      ) : null}
    </div>
  )
}
